import React, { useId, memo } from 'react';
import { motion, useScroll, useVelocity, useSpring, useTransform } from 'framer-motion';
import { DownloadIcon, ShieldCheckIcon, MessageSquareIcon, ClockIcon, PhoneIcon, UsersIcon, SettingsIcon, PlusIcon } from 'lucide-react';
import { Waveform } from './Waveform';
import { DecodeText } from './DecodeText';
import { Tilt } from './Tilt';
import { useIdle } from './useIdle';
const headlineWords = ['Never', 'forget', 'another', 'borrower', 'conversation.'];
export function Hero() {
  const {
    scrollY
  } = useScroll();
  const velocity = useVelocity(scrollY);
  const smoothVelocity = useSpring(velocity, {
    stiffness: 100,
    damping: 30
  });
  const waveScale = useTransform(smoothVelocity, [-1500, 0, 1500], [1.25, 1, 1.25]);
  const waveOpacity = useTransform(smoothVelocity, [-1500, 0, 1500], [0.9, 0.55, 0.9]);
  const idle = useIdle(9000);
  return <section className="relative overflow-hidden texture-dots pt-32 pb-24 lg:flex lg:min-h-screen lg:items-center lg:pt-16 lg:pb-16" data-id="element-203">
      {/* Ambient waveform under the headline — reacts to scroll velocity, calms when the visitor goes quiet */}
      <motion.div style={{
      scaleY: waveScale,
      opacity: waveOpacity,
      transformOrigin: 'bottom'
    }} className="pointer-events-none absolute bottom-0 left-1/2 h-48 w-[140%] -translate-x-1/2 lg:h-64" data-id="element-204">
        <motion.div animate={{
        scaleY: idle ? 0.25 : 1,
        opacity: idle ? 0.35 : 1
      }} transition={{
        type: 'spring',
        stiffness: 60,
        damping: 18
      }} style={{
        transformOrigin: 'bottom'
      }} className="h-full w-full" data-id="element-205">
          <Waveform bars={48} animated barClassName="bg-teal-500/20" className="h-full w-full" data-id="element-206" />
        </motion.div>
      </motion.div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-207">
        <div className="grid items-center gap-16 lg:grid-cols-2" data-id="element-208">
          <div data-id="element-209">
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.4
          }} className="font-mono-brand text-xs uppercase tracking-[0.2em] text-teal-600" data-id="element-210">
              // desktop_ai_notetaker for loan officers
              <span className="caret-blink ml-1 inline-block h-3.5 w-[7px] translate-y-0.5 bg-teal-600" data-id="element-211" />
            </motion.p>

            <h1 className="font-display mt-6 text-4xl font-bold tracking-tight text-ink sm:text-5xl lg:text-6xl xl:text-7xl lg:leading-[1.02]" data-id="element-212">
              {headlineWords.map((word, i) => <motion.span key={word} initial={{
              opacity: 0,
              y: 28
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.55,
              delay: 0.15 + i * 0.12,
              ease: 'easeOut'
            }} className={`mr-[0.28em] inline-block ${word === 'forget' ? 'text-teal-600' : ''}`} data-id="element-213">
                  {word === 'forget' ? <DecodeText text={word} data-id="element-214" /> : word}
                </motion.span>)}
            </h1>

            <motion.p initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.9
          }} className="mt-6 max-w-xl text-lg leading-8 text-slate-600" data-id="element-215">
              Recall is a desktop AI notetaker for loan officers. Works with any
              dialer, organizes every call by borrower, and gives you instant
              answers from your conversation history.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 1.05
          }} className="mt-8 flex flex-col gap-3 sm:flex-row" data-id="element-216">
              <a href="https://github.com/Nimbus-Cloud-Solutions/recall-ai-install/releases/latest/download/Recall-Setup.exe" className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-6 py-3.5 text-base font-bold text-white hover:bg-teal-700 transition-colors" data-id="element-217">
                <DownloadIcon className="h-5 w-5" aria-hidden="true" data-id="element-218" />
                Download for Windows
                <span className="font-mono-brand ml-1 whitespace-nowrap rounded border border-white/30 bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-id="element-219">
                  latest
                </span>
              </a>
            </motion.div>

            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 1.2
          }} className="mt-5 flex items-center gap-2 text-sm text-slate-500" data-id="element-220">
              <ShieldCheckIcon className="h-4 w-4 text-teal-600" aria-hidden="true" data-id="element-221" />
              You control capture. Your data stays yours.
            </motion.p>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.5,
          ease: 'easeOut'
        }} aria-hidden="true" data-id="element-222">
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-223">
              <Tilt data-id="element-224">
                <AppMockup data-id="element-225" />
              </Tilt>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}
const sidebarNav = [{
  icon: MessageSquareIcon,
  label: 'Chat',
  active: true
}, {
  icon: ClockIcon,
  label: 'Today',
  active: false
}, {
  icon: PhoneIcon,
  label: 'Call Note',
  active: false
}, {
  icon: UsersIcon,
  label: 'Borrowers',
  active: false
}, {
  icon: SettingsIcon,
  label: 'Settings',
  active: false
}];
function AppMockup() {
  return <div className="relative" data-id="element-226">
      {/* Soft teal ambient shadow */}
      <div className="glow-teal absolute inset-0 -z-10 rounded-xl" data-id="element-227" />

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-200/60 ring-1 ring-slate-100" data-id="element-228">
        {/* Window chrome */}
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2" data-id="element-229">
          <div className="flex items-center gap-1.5" data-id="element-230">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" data-id="element-231" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" data-id="element-232" />
            <span className="h-2.5 w-2.5 rounded-full bg-teal-400" data-id="element-233" />
          </div>
          <span className="text-[11px] font-medium text-slate-500" data-id="element-234">Recall</span>
          <span className="w-12" data-id="element-235" />
        </div>

        <div className="flex" data-id="element-236">
          {/* Sidebar */}
          <div className="hidden w-[34%] flex-col border-r border-slate-100 bg-white p-3 sm:flex" data-id="element-237">
            <div className="flex items-center gap-2" data-id="element-238">
              <img src="https://cdn.magicpatterns.com/uploads/2Prbqgfyj1rGe5WbKHUdVy/image.png" alt="" className="h-7 w-7 rounded-md" data-id="element-239" />
              <div className="leading-tight" data-id="element-240">
                <p className="text-xs font-bold text-slate-900" data-id="element-241">Recall</p>
                <p className="text-[9px] text-slate-500" data-id="element-242">
                  Borrower intelligence
                </p>
              </div>
            </div>
            <ul className="mt-4 space-y-0.5" data-id="element-243">
              {sidebarNav.map(item => <li key={item.label} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-semibold ${item.active ? 'bg-slate-100 text-blue-900 border-l-2 border-teal-500' : 'text-slate-500'}`} data-id="element-244">
                  <item.icon className="h-3 w-3" data-id="element-245" />
                  {item.label}
                </li>)}
            </ul>
            <div className="mt-auto space-y-1.5 pt-6" data-id="element-246">
              <p className="flex items-center gap-1.5 text-[10px] font-medium text-slate-500" data-id="element-247">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" data-id="element-248" />
                Not recording
              </p>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-teal-50 px-2 py-0.5 text-[10px] font-semibold text-teal-700" data-id="element-249">
                <Waveform bars={5} animated barClassName="bg-teal-500" className="h-2.5 w-5" data-id="element-250" />
                Listening for calls
              </span>
              <p className="text-[9px] text-slate-400" data-id="element-251">
                Recall · Mortgage borrower memory
              </p>
            </div>
          </div>

          {/* Main chat area */}
          <div className="flex-1 bg-slate-50 p-4" data-id="element-252">
            <p className="text-base font-extrabold text-slate-900" data-id="element-253">
              Good afternoon, Zeyad
            </p>
            <p className="mt-0.5 text-[10px] text-slate-500" data-id="element-254">
              Ask Recall about your borrowers, a single relationship, or one
              specific call.
            </p>

            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-id="element-255">
              <div className="flex justify-end" data-id="element-256">
                <p className="max-w-[85%] rounded-lg rounded-br-sm bg-blue-900 px-2.5 py-1.5 text-[10px] font-medium text-white" data-id="element-257">
                  Summarize my most recent borrower conversations
                </p>
              </div>
              <div className="mt-2.5 max-w-[92%] rounded-lg rounded-bl-sm border border-slate-200 bg-white p-2.5 shadow-sm" data-id="element-258">
                <p className="text-[10px] leading-relaxed text-slate-700" data-id="element-259">
                  Zeyad expressed interest in purchasing his first home within a
                  six-month timeline and wants to secure a mortgage. Follow-up:
                  gather income details and property preferences.
                </p>
              </div>
              <div className="mt-2 max-w-[92%] rounded-md border-l-2 border-teal-500 bg-slate-100 px-2.5 py-2" data-id="element-260">
                <p className="text-[9px] font-bold uppercase tracking-wide text-slate-500" data-id="element-261">
                  Zeyad Borrower · 2026-06-10
                </p>
                <p className="mt-0.5 text-[9px] italic leading-snug text-slate-600" data-id="element-262">
                  1. **Short Call Summary**: The borrower expressed interest in
                  purchasing their first home within a six-month…
                </p>
              </div>

              <div className="mt-3 flex items-center gap-1.5" data-id="element-263">
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-medium text-slate-600" data-id="element-264">
                  Asking about{' '}
                  <span className="font-bold text-blue-900" data-id="element-265">
                    · All borrowers
                  </span>
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-1.5" data-id="element-266">
                <span className="flex-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[10px] text-slate-400" data-id="element-267">
                  Message Recall...
                </span>
                <span className="rounded-md bg-blue-900 px-2.5 py-1.5 text-[10px] font-semibold text-white" data-id="element-268">
                  Send
                </span>
              </div>
            </div>

            {/* Recent calls */}
            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3" data-id="element-269">
              <div className="flex items-center justify-between" data-id="element-270">
                <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400" data-id="element-271">
                  Recent calls
                </p>
                <span className="inline-flex items-center gap-1 rounded-md bg-blue-900 px-2 py-1 text-[9px] font-semibold text-white" data-id="element-272">
                  <PlusIcon className="h-2.5 w-2.5" data-id="element-273" />
                  Start a call note
                </span>
              </div>
              <ul className="mt-2 space-y-1.5" data-id="element-274">
                {[{
                name: 'Michael Carter',
                meta: '6/10/2026 · 00:07'
              }, {
                name: 'David Reyes',
                meta: '6/9/2026 · 02:34'
              }, {
                name: 'Zeyad Borrower',
                meta: '6/9/2026 · 01:06'
              }].map(call => <li key={call.name} className="flex items-center justify-between" data-id="element-275">
                    <span className="flex items-center gap-1.5" data-id="element-276">
                      <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-500 text-[8px] font-bold text-white" data-id="element-277">
                        {call.name[0]}
                      </span>
                      <span className="text-[10px] font-semibold text-slate-700" data-id="element-278">
                        {call.name}
                      </span>
                    </span>
                    <span className="text-[9px] text-slate-400" data-id="element-279">
                      {call.meta}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Call-detection toast */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 1.4
    }} className="absolute -bottom-7 right-4 flex items-start gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 shadow-xl shadow-slate-300/60" data-id="element-280">
        <img src="https://cdn.magicpatterns.com/uploads/2Prbqgfyj1rGe5WbKHUdVy/image.png" alt="" className="h-8 w-8 flex-none rounded-md" data-id="element-281" />
        <div data-id="element-282">
          <p className="text-xs font-bold text-slate-900" data-id="element-283">
            Looks like a call started
          </p>
          <p className="text-[10px] text-slate-500" data-id="element-284">
            Capture this call as a borrower note?
          </p>
          <div className="mt-2 flex gap-1.5" data-id="element-285">
            <span className="rounded-md bg-blue-900 px-2.5 py-1 text-[10px] font-semibold text-white" data-id="element-286">
              Start Call Note
            </span>
            <span className="rounded-md border border-slate-200 px-2.5 py-1 text-[10px] font-semibold text-slate-600" data-id="element-287">
              Not now
            </span>
          </div>
        </div>
      </motion.div>
    </div>;
}